/**
 * AnnualReviewReport.js — Year-in-Review printable report
 *
 * Brokerage-style annual summary with time range selector chips (YTD, 1Y, 3Y, Prev Year).
 * Shows financial summary hero, monthly spending trend chart, 50/30/20 averages,
 * monthly breakdown table, money on fire total, and net worth growth.
 *
 * Phase 9G: Annual Year-in-Review report for the Reports page.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getBudgetHistoryRange,
  getNetWorthSnapshotsRange,
  getUserConfig,
} from '../../services/db';
import {
  formatCurrency,
  formatCompact,
  getMonthKey,
  shiftMonthKey,
  formatMonthLabel,
} from '../../utils/format';
import theme from '../../utils/theme';

/** Short month name from "YYYY-MM" key */
function shortMonth(monthKey) {
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short' });
}

/** Compute start/end month keys for each range preset */
function getRangeBounds(rangeKey) {
  const now = new Date();
  const currentMonth = getMonthKey(now);
  const currentYear = now.getFullYear();

  switch (rangeKey) {
    case 'ytd':
      return { start: `${currentYear}-01`, end: currentMonth };
    case '1y':
      return { start: shiftMonthKey(currentMonth, -11), end: currentMonth };
    case '3y':
      return { start: shiftMonthKey(currentMonth, -35), end: currentMonth };
    case 'prev':
      return { start: `${currentYear - 1}-01`, end: `${currentYear - 1}-12` };
    default:
      return { start: `${currentYear}-01`, end: currentMonth };
  }
}

const RANGE_OPTIONS = [
  { key: 'ytd', label: 'YTD' },
  { key: '1y', label: '1Y' },
  { key: '3y', label: '3Y' },
  { key: 'prev', label: 'Prev Year' },
];

// Chart layout constants (matches SpendingTrends pattern)
const VIEW_W = 300;
const VIEW_H = 170;
const CHART_L = 40;
const CHART_R = 280;
const CHART_T = 10;
const CHART_B = 130;
const CHART_W = CHART_R - CHART_L;
const CHART_H = CHART_B - CHART_T;

const COLORS = {
  need: theme.bucketNeed,
  want: theme.bucketWant,
  savings: theme.bucketSavings,
};

export default function AnnualReviewReport() {
  const { user } = useAuth();
  const [range, setRange] = useState('ytd');
  const [budgetData, setBudgetData] = useState([]);
  const [nwSnapshots, setNwSnapshots] = useState([]);
  const [conservativeIncome, setConservativeIncome] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { start, end } = getRangeBounds(range);
    const [budget, nw, config] = await Promise.all([
      getBudgetHistoryRange(user.id, start, end),
      getNetWorthSnapshotsRange(user.id, start, end),
      getUserConfig(user.id),
    ]);
    setBudgetData(budget);
    setNwSnapshots(nw);
    setConservativeIncome(config?.conservative_income || 0);
    setLoading(false);
  }, [user, range]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="report-content">
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.75rem' }}>Year in Review</h3>
        <div className="annual-range-selector">
          {RANGE_OPTIONS.map(r => (
            <button key={r.key}
              className={`annual-range-chip ${range === r.key ? 'active' : ''}`}
              onClick={() => setRange(r.key)}
            >{r.label}</button>
          ))}
        </div>
        <div className="skeleton-card" style={{ height: '200px' }} />
      </div>
    );
  }

  // Aggregate totals
  const totals = budgetData.reduce((acc, bp) => {
    acc.income += bp.income_actual || 0;
    acc.need += Math.abs(bp.need_actual || 0);
    acc.want += Math.abs(bp.want_actual || 0);
    acc.savings += Math.abs(bp.savings_actual || 0);
    acc.fire += bp.money_on_fire || 0;
    return acc;
  }, { income: 0, need: 0, want: 0, savings: 0, fire: 0 });

  const totalSpending = totals.need + totals.want;
  const totalSaved = totals.income - totalSpending;
  const savingsRate = totals.income > 0 ? (totalSaved / totals.income) * 100 : 0;

  // Monthly targets from conservative income
  const monthlyNeedTarget = conservativeIncome * 0.5;
  const monthlyWantTarget = conservativeIncome * 0.3;
  const monthlySavingsTarget = conservativeIncome * 0.2;

  // Average monthly actuals
  const monthCount = budgetData.length || 1;
  const avgNeed = totals.need / monthCount;
  const avgWant = totals.want / monthCount;
  const avgSavings = totals.savings / monthCount;

  // Stacked bar chart data
  const bars = budgetData.map(bp => ({
    monthKey: bp.monthKey,
    need: Math.abs(bp.need_actual || 0),
    want: Math.abs(bp.want_actual || 0),
    savings: Math.abs(bp.savings_actual || 0),
    total: Math.abs(bp.need_actual || 0) + Math.abs(bp.want_actual || 0) + Math.abs(bp.savings_actual || 0),
  }));
  const maxTotal = Math.max(...bars.map(b => b.total), 1);
  const yMax = maxTotal * 1.1;
  const yTicks = [0, yMax / 2, yMax].map(v => ({
    value: v,
    y: CHART_T + (1 - v / yMax) * CHART_H,
    label: formatCompact(Math.round(v)),
  }));

  const barCount = bars.length || 1;
  const totalBarSpace = CHART_W / barCount;
  const barWidth = Math.min(totalBarSpace * 0.7, 35);
  const barOffset = (totalBarSpace - barWidth) / 2;

  // Net worth growth
  const hasNW = nwSnapshots.length >= 1;
  const nwStart = hasNW ? nwSnapshots[0] : null;
  const nwEnd = hasNW ? nwSnapshots[nwSnapshots.length - 1] : null;
  const nwChange = hasNW ? nwEnd.net_worth - nwStart.net_worth : 0;
  const nwChangePct = hasNW && nwStart.net_worth !== 0
    ? (nwChange / Math.abs(nwStart.net_worth)) * 100 : 0;

  // Range label for header
  const { start, end } = getRangeBounds(range);
  const rangeLabel = `${formatMonthLabel(start)} - ${formatMonthLabel(end)}`;

  return (
    <div className="report-content">
      {/* Print-only header */}
      <div className="report-header">
        <div className="report-header-title">Year in Review</div>
        <div className="report-header-subtitle">{rangeLabel}</div>
      </div>

      {/* Visible header (hidden on print) */}
      <div className="annual-screen-header">
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.15rem' }}>Year in Review</h3>
        <p style={{ fontSize: '0.8rem', color: theme.textSecondary, marginBottom: '0.75rem' }}>{rangeLabel}</p>
      </div>

      {/* Range selector */}
      <div className="annual-range-selector">
        {RANGE_OPTIONS.map(r => (
          <button key={r.key}
            className={`annual-range-chip ${range === r.key ? 'active' : ''}`}
            onClick={() => setRange(r.key)}
          >{r.label}</button>
        ))}
      </div>

      {budgetData.length === 0 ? (
        <div className="empty-state">No budget data for this period.</div>
      ) : (
        <>
          {/* 1. Financial Summary Hero */}
          <div className="report-section">
            <div className="report-section-title">Financial Summary</div>
            <div className="annual-summary-grid">
              <div className="annual-summary-metric">
                <div className="metric-value">{formatCurrency(totals.income, false)}</div>
                <div className="metric-label">Total Income</div>
              </div>
              <div className="annual-summary-metric">
                <div className="metric-value">{formatCurrency(totalSpending, false)}</div>
                <div className="metric-label">Total Spending</div>
              </div>
              <div className="annual-summary-metric">
                <div className={`metric-value ${totalSaved >= 0 ? 'success' : 'danger'}`}>
                  {formatCurrency(totalSaved, false)}
                </div>
                <div className="metric-label">Total Saved</div>
              </div>
              <div className="annual-summary-metric">
                <div className={`metric-value ${savingsRate >= 20 ? 'success' : savingsRate >= 10 ? '' : 'danger'}`}>
                  {savingsRate.toFixed(1)}%
                </div>
                <div className="metric-label">Savings Rate</div>
              </div>
            </div>
          </div>

          {/* 2. Monthly Spending Trend */}
          {bars.length >= 2 && (
            <div className="report-section">
              <div className="report-section-title">Monthly Spending</div>
              <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} style={{ width: '100%', display: 'block' }}>
                {yTicks.map((t, i) => (
                  <line key={i} x1={CHART_L} y1={t.y} x2={CHART_R} y2={t.y}
                    stroke={theme.border} strokeWidth="0.5" strokeDasharray="3,2" />
                ))}
                {bars.map((bar, i) => {
                  const x = CHART_L + i * totalBarSpace + barOffset;
                  const needH = (bar.need / yMax) * CHART_H;
                  const wantH = (bar.want / yMax) * CHART_H;
                  const savingsH = (bar.savings / yMax) * CHART_H;
                  const needY = CHART_B - needH;
                  const wantY = needY - wantH;
                  const savingsY = wantY - savingsH;
                  return (
                    <g key={i}>
                      {bar.need > 0 && <rect x={x} y={needY} width={barWidth} height={needH} fill={COLORS.need} rx="2" opacity="0.85" />}
                      {bar.want > 0 && <rect x={x} y={wantY} width={barWidth} height={wantH} fill={COLORS.want} rx="0" opacity="0.85" />}
                      {bar.savings > 0 && <rect x={x} y={savingsY} width={barWidth} height={savingsH} fill={COLORS.savings} rx="2" opacity="0.85" />}
                    </g>
                  );
                })}
                {yTicks.map((t, i) => (
                  <text key={`y${i}`} x={CHART_L - 4} y={t.y + 3} textAnchor="end"
                    style={{ fontSize: '7px', fill: theme.textMuted, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                    {t.label}
                  </text>
                ))}
                {bars.map((bar, i) => (
                  <text key={`x${i}`}
                    x={CHART_L + i * totalBarSpace + totalBarSpace / 2}
                    y={CHART_B + 14} textAnchor="middle"
                    style={{ fontSize: bars.length > 18 ? '5px' : '7px', fill: theme.textMuted, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                    {shortMonth(bar.monthKey)}
                  </text>
                ))}
              </svg>
              <div className="trends-legend" style={{ marginTop: '0.25rem' }}>
                <span className="cashflow-legend-item"><span className="trends-legend-dot need" /> Needs</span>
                <span className="cashflow-legend-item"><span className="trends-legend-dot want" /> Wants</span>
                <span className="cashflow-legend-item"><span className="trends-legend-dot savings" /> Savings</span>
              </div>
            </div>
          )}

          {/* 3. 50/30/20 Annual Averages */}
          {conservativeIncome > 0 && (
            <div className="report-section">
              <div className="report-section-title">Monthly Averages vs. 50/30/20 Targets</div>
              <div className="annual-avg-bars">
                <AvgBar label="Needs" actual={avgNeed} target={monthlyNeedTarget} pct={50} color={COLORS.need} />
                <AvgBar label="Wants" actual={avgWant} target={monthlyWantTarget} pct={30} color={COLORS.want} />
                <AvgBar label="Savings" actual={avgSavings} target={monthlySavingsTarget} pct={20} color={COLORS.savings} />
              </div>
            </div>
          )}

          {/* 4. Monthly Breakdown Table */}
          <div className="report-section">
            <div className="report-section-title">Monthly Breakdown</div>
            <div style={{ overflowX: 'auto' }}>
              <table className="annual-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Income</th>
                    <th>Needs</th>
                    <th>Wants</th>
                    <th>Savings</th>
                    <th>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetData.map(bp => {
                    const need = Math.abs(bp.need_actual || 0);
                    const want = Math.abs(bp.want_actual || 0);
                    const sav = Math.abs(bp.savings_actual || 0);
                    const income = bp.income_actual || 0;
                    const net = income - need - want;
                    const overNeed = conservativeIncome > 0 && need > monthlyNeedTarget;
                    const overWant = conservativeIncome > 0 && want > monthlyWantTarget;
                    return (
                      <tr key={bp.monthKey}>
                        <td>{shortMonth(bp.monthKey)}</td>
                        <td>{formatCurrency(income, false)}</td>
                        <td className={overNeed ? 'over-budget' : ''}>{formatCurrency(need, false)}</td>
                        <td className={overWant ? 'over-budget' : ''}>{formatCurrency(want, false)}</td>
                        <td>{formatCurrency(sav, false)}</td>
                        <td className={net >= 0 ? 'under-budget' : 'over-budget'}>{formatCurrency(net, false)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td>{formatCurrency(totals.income, false)}</td>
                    <td>{formatCurrency(totals.need, false)}</td>
                    <td>{formatCurrency(totals.want, false)}</td>
                    <td>{formatCurrency(totals.savings, false)}</td>
                    <td className={totalSaved >= 0 ? 'under-budget' : 'over-budget'}>
                      {formatCurrency(totalSaved, false)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* 5. Money on Fire */}
          {totals.fire > 0 && (
            <div className="report-section">
              <div className="report-section-title">Money on Fire</div>
              <div className="annual-summary-metric" style={{ maxWidth: '200px' }}>
                <div className="metric-value danger">{formatCurrency(totals.fire, false)}</div>
                <div className="metric-label">Late Fees & Interest</div>
              </div>
            </div>
          )}

          {/* 6. Net Worth Growth */}
          {hasNW && nwSnapshots.length >= 2 && (
            <div className="report-section">
              <div className="report-section-title">Net Worth Growth</div>
              <div className="annual-nw-growth">
                <div className="annual-nw-point">
                  <div className="nw-val">{formatCurrency(nwStart.net_worth, false)}</div>
                  <div className="nw-label">{shortMonth(nwStart.month_key)}</div>
                </div>
                <div className="annual-nw-arrow">→</div>
                <div className="annual-nw-point">
                  <div className="nw-val">{formatCurrency(nwEnd.net_worth, false)}</div>
                  <div className="nw-label">{shortMonth(nwEnd.month_key)}</div>
                </div>
              </div>
              <div className="annual-nw-change">
                <span className={`nw-delta ${nwChange >= 0 ? 'positive' : 'negative'}`}>
                  {nwChange >= 0 ? '+' : ''}{formatCurrency(nwChange, false)} ({nwChangePct >= 0 ? '+' : ''}{nwChangePct.toFixed(1)}%)
                </span>
              </div>
              {/* Mini net worth chart */}
              <MiniNetWorthChart snapshots={nwSnapshots} />
            </div>
          )}

          {/* 7. Footer */}
          <div className="report-footer">
            Generated {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </div>
        </>
      )}
    </div>
  );
}

/** Average bar with actual vs target */
function AvgBar({ label, actual, target, pct, color }) {
  const maxVal = Math.max(actual, target, 1);
  const actualPct = (actual / maxVal) * 100;
  const targetPct = (target / maxVal) * 100;
  const over = actual > target;

  return (
    <div className="annual-avg-row">
      <div className="annual-avg-header">
        <span className="annual-avg-label">{label} ({pct}%)</span>
        <span className="annual-avg-value">
          {formatCurrency(actual, false)} / {formatCurrency(target, false)}
          {over && <span style={{ color: theme.danger, marginLeft: '0.25rem', fontSize: '0.75rem' }}>over</span>}
        </span>
      </div>
      <div className="report-bar">
        <div className="report-bar-fill" style={{ width: `${Math.min(actualPct, 100)}%`, background: over ? theme.danger : color }} />
        {/* Target marker */}
        <div style={{
          position: 'absolute', left: `${Math.min(targetPct, 100)}%`, top: 0, bottom: 0,
          width: '2px', background: theme.primary, opacity: 0.4,
        }} />
      </div>
    </div>
  );
}

/** Compact net worth line chart for the annual review */
function MiniNetWorthChart({ snapshots }) {
  if (!snapshots || snapshots.length < 2) return null;

  const W = 300, H = 80, L = 10, R = 290, T = 5, B = 70;
  const CW = R - L, CH = B - T;
  const values = snapshots.map(s => s.net_worth);
  const maxV = Math.max(...values);
  const minV = Math.min(...values);
  const pad = Math.max((maxV - minV) * 0.15, 500);
  const yMax = maxV + pad, yMin = minV - pad;
  const yRange = yMax - yMin || 1;
  const n = snapshots.length;

  const toX = (i) => L + (i / (n - 1)) * CW;
  const toY = (v) => T + (1 - (v - yMin) / yRange) * CH;

  const linePoints = snapshots.map((s, i) => `${toX(i)},${toY(s.net_worth)}`).join(' ');
  const areaPoints = linePoints + ` ${toX(n - 1)},${B} ${toX(0)},${B}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block', marginTop: '0.25rem' }}>
      <defs>
        <linearGradient id="annual-nw-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={theme.success} stopOpacity="0.2" />
          <stop offset="100%" stopColor={theme.success} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill="url(#annual-nw-grad)" />
      <polyline points={linePoints} fill="none" stroke={theme.success} strokeWidth="1.5" strokeLinejoin="round" />
      {snapshots.map((s, i) => (
        <circle key={i} cx={toX(i)} cy={toY(s.net_worth)} r="2" fill={theme.success} stroke="white" strokeWidth="0.5" />
      ))}
    </svg>
  );
}
